import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { AuthCard, authLinkClass } from '../components/auth-card';
import { Button } from '../components/button';
import { useRequestPasswordReset } from '../features/account/api';

/**
 * Forgot password (/forgot-password): asks the portal to email a one-hour reset link to the
 * address, if it belongs to an active account (DECISIONS.md A-AC1). The answer is the same whether
 * or not it does, so this page cannot be used to find out who has an account. Nothing changes on
 * the account until the link is used.
 */
const formSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .max(254, 'That email address is too long.')
    .email('Enter a valid email address.'),
});
type Form = z.infer<typeof formSchema>;

export function ForgotPasswordPage() {
  const request = useRequestPasswordReset();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(formSchema) });

  const backToSignIn = (
    <p className="mt-7 border-t border-line pt-5 text-center text-sm">
      <Link to="/login" className={authLinkClass}>
        Back to sign in
      </Link>
    </p>
  );

  if (request.isSuccess) {
    return (
      <AuthCard title="Check your email">
        <p role="status" className="mt-4 text-sm text-fg-2">
          If an active Bekebi Portal account uses that address, a link to choose a new password is
          on its way. It works once, for one hour. Your current password keeps working until you use
          the link.
        </p>
        <p className="mt-3 text-sm text-muted">
          Nothing arrived after a few minutes? Check your spam folder, or ask the portal
          administrator.
        </p>
        {backToSignIn}
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Reset your password">
      <p className="mt-2 text-center text-sm text-muted">
        Enter your account&rsquo;s email address and we will email you a link to choose a new
        password.
      </p>
      {request.isError ? (
        <p
          role="alert"
          className="mt-6 rounded-lg border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
        >
          {request.error instanceof ApiError
            ? request.error.message
            : 'The request could not be sent. Try again in a moment.'}
        </p>
      ) : null}
      <form
        onSubmit={handleSubmit((values) => request.mutate({ email: values.email }))}
        noValidate
        className="mt-6 space-y-5"
        aria-label="Reset password"
      >
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-fg">
            Email address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className="mt-1.5 block h-11 w-full border px-3.5 text-sm"
            {...register('email')}
          />
          {errors.email ? (
            <p id="email-error" className="mt-1.5 text-sm text-danger">
              {errors.email.message}
            </p>
          ) : null}
        </div>
        <Button type="submit" className="h-11 w-full" disabled={request.isPending}>
          {request.isPending ? 'Sending…' : 'Email me a link'}
        </Button>
      </form>
      {backToSignIn}
    </AuthCard>
  );
}
